import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges,
  OnInit, Output, ChangeDetectorRef, OnDestroy, SimpleChanges} from '@angular/core';
import {BaseComponent} from '../../components/general/base/base.component';
import {Point} from 'geojson';
import {MapElement} from '../../shared/map/map-element.model';
import {LinkingService} from '../../linking/linking.service';
import {AuthService} from '../../auth/auth.service';
import { isFollowingAvailableForFeature } from '../../custom/vrgroup/follow-train/follow-utils';
import { Layer } from '../../shared/map/layer/layer.model';
import { last } from 'lodash-es';
import { InfluxMetadataMisonetService } from '~app/influx/influx-metadata/influx-metadata-misonet.service';

import { V2LService } from '~app/v2l/v2l.service';

interface VisibleButtonConfig {
  info: boolean;
  focus: boolean;
  flag: boolean;
  follow: boolean;
  misoMeasurements: boolean;
  v2lMeasurements: boolean;
  cargoManifest: boolean;
}

@Component({
  selector: 'qm-element-actions',
  templateUrl: './element-actions.component.html',
  styleUrls: ['./element-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ElementActionsComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  public selectedFeature: MapElement<Point>;
  @Input()
  public selectedLayer: Layer;
  @Input()
  public qmStyle: { [key: string]: string; };
  @Input()
  public visibleButtons: VisibleButtonConfig = {
      info: true,
      focus: true,
      flag: true,
      follow: true,
      misoMeasurements: true,
      v2lMeasurements: true,
      cargoManifest: false,
  };
  @Output()
  public detailsOpened: EventEmitter<void> = new EventEmitter();

  public vivoId: string = null;
  /**
   * Username
   */
  public userName: string;
  private vivoType: string = 'Asset';

  public tenant: number;
  public showVrInfo: boolean = true;
  public hasMisoMeasurements: boolean = false;
  public hasV2lMeasurements: boolean = false;

  private closed: boolean = false;

  /**
   * @param {MapService} mapService
   * @param {VivoService} vivoService
   */
  constructor(
    private readonly linkingService: LinkingService,
    private readonly influxMetadataMisonetService: InfluxMetadataMisonetService,
    private readonly v2lService: V2LService,
    private readonly auth: AuthService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    super();
    this.name = 'ElementActionsComponent';
    this.userName = this.auth.getSubject();
    this.tenant = this.auth.getTenant();
  }

  ngOnInit() {
    this.updateVivoMapping();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateVivoMapping();
    const isSelectedFeatureOnMap = this.selectedFeature?.geometry?.coordinates
      && this.selectedFeature.geometry.coordinates[0] !== 0;
    this.visibleButtons.focus = isSelectedFeatureOnMap;
    this.visibleButtons.follow = isFollowingAvailableForFeature(this.selectedFeature) && isSelectedFeatureOnMap;
    //// mr-797, not all layers are flaggable, maybe re-worked later on
    //// vr: junat, kalusto.., joensuu: kalusto
    this.visibleButtons.flag = !this.selectedFeature.layer?.metadata?.flaggingDisabled;

    if (changes.selectedFeature || changes.selectedLayer) {
      this.updateHasMisoMeasurements();
      this.updateHasV2lMeasurements();
    }
  }

  ngOnDestroy() {
    this.closed = true;
  }

  onDetailsOpened() {
    this.detailsOpened.emit();
  }

  public setVrInfoVisibility(show: boolean) {
    this.showVrInfo = show;
    if (!this.closed) {
      this.cdr.detectChanges();
    }
  }

  private async updateVivoMapping(): Promise<void> {
    if (this.selectedFeature) {
      // Use feature's id in cases that selectedLayer is not defined
      const regoId = this.selectedLayer !== undefined
        ? this.selectedFeature.properties[this.selectedLayer.metadata.uniqueBy]
        : this.selectedFeature.id;
      // TODO: handle multiple linkings
      const linkings = await this.linkingService.getLinkingsForRegoId(regoId, 'vivo');
      if (linkings.length > 0) {
        this.vivoId = last(linkings).sourceId;
        this.vivoType = last(linkings).sourceLinkType;
        this.cdr.detectChanges(); //// to get VIVO link visible ??
      }
    }
  }

  /**
   * check V2L with coordinates
   * @returns
   */
  private async updateHasV2lMeasurements(): Promise<void> {
    this.hasV2lMeasurements = false;
    this.cdr.markForCheck();

    const config = this.auth.getTenantConfig();
    if (!config.v2lMeasurements) {
      // this tenant doesn't use v2l measurements at all
      return;
    }
    this.hasV2lMeasurements = !!await this.v2lService.getHasAnalysis(this.selectedFeature);
    this.cdr.markForCheck();
  }

  /**
   * check with element name from misonet metadataservice
   */
  private async updateHasMisoMeasurements(): Promise<void> {

    this.hasMisoMeasurements = false;
    this.cdr.markForCheck();

    const config = this.auth.getTenantConfig();
    if (!config.influxMeasurements) {
      // this tenant doesn't use measurements at all
      return;
    }
    const measurementsFilters = this.selectedLayer?.metadata?.measurementsFilters;
    if (measurementsFilters) {
      const filters = {};
      for (const [fieldName, propertyName] of Object.entries(measurementsFilters)) {
        filters[fieldName] = this.selectedFeature.properties[propertyName];
      }

      //// IF misonet metadata exist for selected feature, also related 'numeric' data should always be present!
      const misoMeta = await this.influxMetadataMisonetService.getDistinctValuesWithLabel('tunnus', 'nimi',filters);
      if (misoMeta) {
        this.hasMisoMeasurements = true;
      }
      this.cdr.markForCheck();
    }
  }

  /**
   * Return link to VIVO for current map object
   * @returns {string}
   */
  public getVivoLink(): string {
    if (this.vivoType === 'Organization') {
      return 'https://miprovivo.trackinno.com/organizationNodes/' + this.vivoId + '/assets';
    } else {
      return 'https://miprovivo.trackinno.com/assets/' + this.vivoId;
    }
  }
}
